import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, HeartPulse } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { ThemePicker } from "@/components/export/ThemePicker";

const inGroup = (r, g) => g === "all" || (g === "mine" ? !r.universe_id : r.universe_id === g);

const download = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const Groups = ({ universes, group, pickGroup }) => {
  const chip = (k, l) => <button key={k} onClick={() => pickGroup(k)} className={`rounded-full px-3 py-1 text-xs transition-colors ${group === k ? "bg-[#D97706] text-[#0B0D10]" : "border border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`} data-testid={`cookbook-export-group-${k}`}>{l}</button>;
  return <div className="flex flex-wrap gap-1.5">{chip("all", "All recipes")}{chip("mine", "My kitchen")}{universes.map((u) => chip(u.id, u.name))}</div>;
};

export const CookbookExport = ({ open, onOpenChange, recipes, universes }) => {
  const [group, setGroup] = useState("all");
  const [sel, setSel] = useState(() => new Set());
  const [title, setTitle] = useState("My Cookbook");
  const [subtitle, setSubtitle] = useState("");
  const [themes, setThemes] = useState([]);
  const [theme, setTheme] = useState("parchment");
  const [busy, setBusy] = useState(false);
  const shown = useMemo(() => recipes.filter((r) => inGroup(r, group)), [recipes, group]);
  const pickGroup = (g) => { setGroup(g); setSel(new Set(recipes.filter((r) => inGroup(r, g)).map((r) => r.id))); const u = universes.find((x) => x.id === g); setTitle(u ? `${u.name} Cookbook` : "My Cookbook"); };
  useEffect(() => { if (open) { pickGroup("all"); if (!themes.length) api.get("/export/themes").then((r) => setThemes(r.data)).catch(() => {}); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = recipes.filter((r) => sel.has(r.id));
  const missing = chosen.filter((r) => !r.has_stats).length;
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recipes/export/pdf", { ids: chosen.map((r) => r.id), title: title.trim() || "My Cookbook", subtitle, theme }, { responseType: "blob" });
      download(data, `${(title.trim() || "cookbook").replace(/[^\w]+/g, "-").toLowerCase()}.pdf`);
      toast.success("Cookbook ready");
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e) || "Couldn't make the PDF"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="cookbook-export-dialog">
        <DialogHeader><DialogTitle>Print a cookbook</DialogTitle><DialogDescription>Pick a group of recipes, untick any you don't want, then download a PDF with a contents page.</DialogDescription></DialogHeader>
        <Groups universes={universes} group={group} pickGroup={pickGroup} />
        <div className="max-h-56 space-y-1 overflow-y-auto rounded border border-[#2D3442] p-2" data-testid="cookbook-export-list">
          {shown.map((r) => (
            <label key={r.id} className="flex items-center gap-2 rounded px-1.5 py-1 text-sm text-[#E8DFD1] hover:bg-[#1A202C]">
              <Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`cookbook-export-item-${r.id}`} />
              <span className="flex-1 truncate">{r.title}</span>
              {r.has_stats && <HeartPulse className="h-3.5 w-3.5 text-[#F87171]" title="Has health stats" />}
              <span className="text-xs text-[#7A7468]">{r.universe_name || "My kitchen"}</span>
            </label>
          ))}
          {!shown.length && <p className="p-2 text-sm text-[#7A7468]">No recipes in this group.</p>}
        </div>
        <p className="text-xs text-[#A8A090]" data-testid="cookbook-export-summary">
          {chosen.length} {chosen.length === 1 ? "recipe" : "recipes"} selected.{missing > 0 && ` ${missing} ${missing === 1 ? "has" : "have"} no saved health stats and will print without them. Open a recipe and tap "Estimate health stats" to add them.`}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1"><span className="lf-label">Title</span><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} data-testid="cookbook-export-title" /></label>
          <label className="space-y-1"><span className="lf-label">Subtitle</span><Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} maxLength={300} placeholder="e.g. Feasts of the Ember Reaches" data-testid="cookbook-export-subtitle" /></label>
        </div>
        {themes.length > 0 && <div className="min-w-0 space-y-2"><span className="lf-label">Page style</span><ThemePicker themes={themes} value={theme} onChange={setTheme} /></div>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cookbook-export-cancel">Cancel</Button>
          <Button onClick={run} disabled={busy || !chosen.length} data-testid="cookbook-export-download"><FileDown className="mr-1.5 h-4 w-4" />{busy ? "Typesetting…" : "Download PDF"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
